import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import Fqicon from "../../assets/img/Fq.png";
import AuthContext from "../../auth/AuthContext";
import LoginService from "../../services/LoginService";
import AlertService from "../../services/AlertService";

export default function Login() {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      authContext?.mfaVerified &&
      !authContext?.resetPassword
    ) {
      navigate("/");
    }
  }, [
    authContext?.mfaVerified,
    authContext?.resetPassword,
    navigate
  ]);


  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getRedirectPath = (data) => {
    if (data.resetPassword) return "/reset";
    if (data.mfaSetupEnabled && !data.mfaSetupCompleted) return "/qrcode";
    if (data.mfaSetupEnabled && data.mfaSetupCompleted) return "/mfa";
    return "/";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      userName: email,
      password,
      clientId: process.env.REACT_APP_CLIENTID,
      clientSecret: process.env.REACT_APP_CLIENTSECRET,
    };

    try {
      const result = await LoginService.Login(payload);

      if (!result.isSuccess) {
        AlertService.error(result.errorMessage);
        return;
      }

      const data = result.data;
      authContext.login(
        data.access_token,
        data.refresh_token,
        data.identifier,
        data.id,
        data.roleId,
        data.isAnswered,
        data.resetPassword,
        data.mfaSetupCompleted,
        data.mfaSetupEnabled,


        data.resetPassword
          ? false
          : data.mfaSetupEnabled
            ? false
            : true,

        data.hasOngoingValidations,
        data.displayName,
        data.menuPermissions,
        data.course_count,
        data.impersonate,
        data.impersonatedUserId
      );

      authContext.setPermissions(data.menuPermissions);
        navigate(getRedirectPath(data));

    } catch (err) {
      console.error(err);
      AlertService.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans">
      {/* LEFT */}
      <div className="hidden md:flex w-1/2 bg-[#0e3a46] relative overflow-hidden">
        <div className="absolute top-12 -right-10 bg-white h-24 w-56 rounded-full flex items-center pl-12 shadow-md">
          <img src={Fqicon} alt="Logo" className="h-12 object-contain" />
        </div>
      </div>

      {/* RIGHT */}
      <div className="flex w-full md:w-1/2 items-center justify-center bg-white px-4">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[420px]"
        >
          <h2 className="text-center text-3xl font-semibold mb-2">
            Sign In to Your Account
          </h2>

          <p className="text-center text-gray-600 mb-6">
            Please enter your details
          </p>

          {/* EMAIL */}
          <div className="mb-5">
            <label className="block text-base font-medium mb-1 text-gray-700">
              Email
            </label>

            <div className={`flex items-center h-12 px-3 border rounded-md
              ${errors.email ? "border-red-500" : "border-gray-300"}`}>
              <FaUser className="text-gray-400 mr-3" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 outline-none"
                placeholder="Email"
              />
            </div>

            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* PASSWORD */}
          <div className="">
            <label className="block text-base font-medium mb-1 text-gray-700">
              Password
            </label>

            <div className={`flex items-center h-12 px-3 border rounded-md
              ${errors.password ? "border-red-500" : "border-gray-300"}`}>
              <FaLock className="text-gray-400 mr-3" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 outline-none"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          {/* FORGOT */}
          <div className="text-right mb-6">
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-sm text-blue-600 hover:underline"
            >
              Forgot password?
            </button>
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#0e3a46] text-white rounded-md font-medium"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}